// ─────────────────────────────────────────────────────────────────────────────
// Sección "Insignias de Campaña" de Ganadores. Grid de tarjetas — a diferencia de la lista en fila
// de Gestión (AdminBadgeList), porque la maqueta de esta pantalla pide tarjetas. El catálogo
// subyacente es distinto (Categoria=Campaign, ver Task 2.1); el contrato de props replica el de
// AdminBadgeList a propósito para que quien lo integre no tenga que aprender dos patrones.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBadgeName } from '@/api/recognition';
import type { AdminBadgeDto, LocalizedText } from '@/api/recognition';
import { ConfirmModal } from '@/components/common';
import { asset } from '@/utils/basePath';

interface AdminCampaignBadgeGridProps {
  badges: AdminBadgeDto[];
  onAdd: () => void;
  onEdit: (badge: AdminBadgeDto) => void;
  /** Igual que en AdminBadgeList: quita la insignia del borrador, el borrado real ocurre al Guardar. */
  onDelete: (badge: AdminBadgeDto) => void;
}

function anioSubtituloVisible(anioSubtitulo: LocalizedText | null | undefined, lang: string): string {
  return anioSubtitulo?.[lang] ?? anioSubtitulo?.es ?? '';
}

export function AdminCampaignBadgeGrid({ badges, onAdd, onEdit, onDelete }: Readonly<AdminCampaignBadgeGridProps>) {
  const { t, i18n } = useTranslation('recognition');
  const [pendingDelete, setPendingDelete] = useState<AdminBadgeDto | null>(null);

  return (
    <div className="adm-field">
      <div className="adm-badge-list-head">
        <span>{t('admin.winners.campaignBadges.title')}</span>
      </div>
      <p className="adm-note">{t('admin.winners.campaignBadges.permanenceNote')}</p>

      {badges.length === 0 && <p className="adm-note">{t('admin.winners.campaignBadges.empty')}</p>}

      <div className="adm-badge-grid">
        {badges.map((badgeItem) => (
          <div key={badgeItem.id} className="adm-badge-card">
            <img src={badgeItem.iconUrl || asset('/color-icon.png')} alt="" className="adm-badge-card-thumb" />
            <span className="adm-badge-card-name">{getBadgeName(badgeItem, i18n.language)}</span>
            <span className="adm-badge-card-year">{anioSubtituloVisible(badgeItem.anioSubtitulo, i18n.language)}</span>
            <div className="adm-badge-card-actions">
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => onEdit(badgeItem)}>
                {t('admin.winners.campaignBadges.edit')}
              </button>
              <button
                type="button"
                className="btn-small btn-danger-sm"
                aria-label={`${t('admin.winners.campaignBadges.delete')}: ${getBadgeName(badgeItem, i18n.language)}`}
                onClick={() => setPendingDelete(badgeItem)}
              >
                <i className="fas fa-trash" />
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="adm-badge-card adm-badge-card--add" onClick={onAdd}>
          <i className="fas fa-plus" aria-hidden="true" />
          <span>{t('admin.winners.campaignBadges.add')}</span>
        </button>
      </div>

      {pendingDelete && (
        <ConfirmModal
          title={t('admin.winners.campaignBadges.delete')}
          message={t('admin.winners.campaignBadges.deleteConfirm', {
            name: getBadgeName(pendingDelete, i18n.language),
          })}
          confirmText={t('admin.winners.campaignBadges.delete')}
          onConfirm={() => onDelete(pendingDelete)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

export default AdminCampaignBadgeGrid;
